import { useMemo, useRef, useState } from 'react'

import { addDays, endOfDay, startOfDay, subDays } from 'date-fns'

import { readFragment } from '~publish/graphql'
import { PostCard_Post } from '~publish/components/PostCard'
import type { PostCard_PostData } from '~publish/components/PostCard'

import type { BoardDateRange } from './useBoardState'
import { usePaginatedPostList } from '../PostList/usePaginatedPostList'
import type { PostStatus } from '../PostList/usePaginatedPostList'

/** How a column's default 30-day windowing behaves before any explicit Date range filter is set. */
export type BoardWindowRule = 'none' | 'upcoming-30-days' | 'past-30-days'

const WINDOW_DAYS = 30

/**
 * Larger than the shared `POSTS_PER_PAGE` (20) — a Kanban column reads
 * mostly-full at a glance rather than needing a scroll-triggered fetch for
 * every extra dozen posts.
 */
export const BOARD_POSTS_PER_PAGE = 50

export type UseBoardColumnPostsOptions = {
  statuses: PostStatus[]
  channelIds?: string[]
  tagIds?: string[]
  /** Overrides the column's default windowing when the Date range filter is set. */
  dateRangeOverride?: BoardDateRange
  windowRule: BoardWindowRule
  /** Skips the query entirely — e.g. a column that can never have any posts. */
  skip?: boolean
}

type PaginatedPostListResult = ReturnType<typeof usePaginatedPostList>

export type UseBoardColumnPostsResult = {
  posts: readonly PostCard_PostData[]
  loading: boolean
  fetchingMore: boolean
  reloading: boolean
  error: PaginatedPostListResult['error']
  hasNextPage: boolean
  lastElementRef: PaginatedPostListResult['lastElementRef']
  /** Whether this column's windowing can be widened further. */
  canShowMore: boolean
  showingAll: boolean
  showMore: () => void
}

/**
 * Wraps `usePaginatedPostList` for one Board column, applying the column's
 * default 30-day window (past for sent-like columns, future for
 * scheduled-like ones) until the column's own "Show more" is clicked, or an
 * explicit Date range filter overrides it for every column at once.
 *
 * `sentAt` has no server-side comparator today, so the "past 30 days" window
 * for Published/Failed columns filters on `dueAt` instead — close enough
 * since a sent post's `dueAt` rarely drifts far from when it went out, but
 * worth a dedicated `sentAt` filter later.
 */
export function useBoardColumnPosts({
  statuses,
  channelIds,
  tagIds,
  dateRangeOverride,
  windowRule,
  skip = false,
}: UseBoardColumnPostsOptions): UseBoardColumnPostsResult {
  const [showingAll, setShowingAll] = useState(false)

  // Quantized to the day and memoized for the component's lifetime — a
  // fresh `new Date()` on every render would change these ISO bounds by a
  // few milliseconds each time, making every render look like a new query
  // to Apollo and re-fetching forever.
  const today = useMemo(() => startOfDay(new Date()), [])
  const dueAt = dateRangeOverride
    ? {
        start: startOfDay(dateRangeOverride.startDate).toISOString(),
        end: endOfDay(dateRangeOverride.endDate).toISOString(),
      }
    : showingAll || windowRule === 'none'
      ? undefined
      : windowRule === 'upcoming-30-days'
        ? { end: addDays(today, WINDOW_DAYS).toISOString() }
        : { start: subDays(today, WINDOW_DAYS).toISOString() }

  // Ascending only for 'upcoming-30-days' — must match the client-side sort
  // in BoardColumnContainer, or the post carrying `lastElementRef` renders
  // at the wrong end of the column and the infinite-scroll sentinel fires
  // immediately, fetching every page on mount.
  const sortPreset =
    windowRule === 'upcoming-30-days'
      ? 'upcomingFirst'
      : 'mostRecentlyPostedFirst'

  const { data, loading, fetchingMore, reloading, error, lastElementRef } =
    usePaginatedPostList({
      status: statuses,
      channelIds,
      tagIds,
      dueAt,
      skip,
      sortPreset,
      perPage: BOARD_POSTS_PER_PAGE,
    })

  // Widening the window (`showMore`) or changing a filter swaps in a new
  // `dueAt`/`channelIds`/`tagIds` variable set, which the cache has no entry
  // for yet — `data` goes briefly undefined while it refetches. Without this,
  // the column would flash empty and repopulate a moment later; keep showing
  // the last real connection (edges and pageInfo together, so the header's
  // count badge doesn't glitch independently of the list) until the new one
  // actually lands.
  const connection = data?.posts
  const lastKnownConnectionRef = useRef(connection)
  if (connection) {
    lastKnownConnectionRef.current = connection
  }
  const resolvedConnection = connection ?? lastKnownConnectionRef.current
  const nodes = resolvedConnection?.edges?.map((edge) => edge.node) ?? []
  const posts = readFragment(PostCard_Post, nodes)

  return {
    posts,
    loading,
    fetchingMore,
    reloading,
    error,
    hasNextPage: resolvedConnection?.pageInfo?.hasNextPage ?? false,
    lastElementRef,
    canShowMore: windowRule !== 'none' && !dateRangeOverride && !showingAll,
    showingAll,
    showMore: () => setShowingAll(true),
  }
}
